import React, { FC, useContext, useEffect, useState } from 'react';
import { Field, FieldProps, Form, Formik } from 'formik';
import * as Yup from 'yup';

import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import InputContainer from 'shared/components/FormInputs/InputContainer';
import InputText from 'shared/components/FormInputs/InputText';
import InputErrorHint from 'shared/components/InputErrorHint';
import Loader from 'shared/components/Loader';
import InputTextArea from 'shared/components/FormInputs/InputTextArea';
import InputPhone from 'shared/components/FormInputs/InputPhone';
import GoogleApiAutoCompleat from 'shared/components/GoogleApiAutoComplete';
import InputLogo from 'components/Organization/InputLogo';
import OrganizationTypeSelect from 'shared/components/Selects/SelectDropdown/OrganizationTypeSelect';

import { OrganizationsTableContext } from 'shared/components/Table/AdminOrgTable';

import {
	emailSchema,
	imagePathSchema,
	organizationDescriptionSchema,
	organizationNameShema,
	phoneNumberSchema,
	temporaryAddressMandatorySchema,
	websiteSchema,
} from 'core/validation';

import { useUpdateOrganization } from 'hooks/organizations';

import { ISearchOrganisationByAdminResponse, IUpdateOrganizationRequest } from '@joc/api-gateway';

import styles from './OrgInfoForm.module.scss';

type OrgInfoFormParentProps = {
	setIsUpdateSuccess: React.Dispatch<React.SetStateAction<boolean>>;
	setIsShowPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

const validationSchema = Yup.object().shape({
	organizationName: organizationNameShema,
	organisationGeneralType: Yup.string().required('Type is required'),
	organizationLogoPath: imagePathSchema,
	organizationDescription: organizationDescriptionSchema,
	organizationPhoneNumber: phoneNumberSchema,
	organizationEmail: emailSchema,
	organizationWebSite: websiteSchema,
	address: temporaryAddressMandatorySchema,
});

const OrgInfoForm: FC<OrgInfoFormParentProps> = ({ setIsUpdateSuccess, setIsShowPopup }) => {
	const organizationData = useContext(OrganizationsTableContext);
	const { updateOrganization, isSuccess, isPending } = useUpdateOrganization();

	const submitClickHandler = (values: ISearchOrganisationByAdminResponse) => {
		updateOrganization({ id: +values.id, values: values as IUpdateOrganizationRequest });
	};

	useEffect(() => {
		if (isSuccess) {
			setIsUpdateSuccess(true);
		}
	}, [isSuccess]);

	return (
		<Formik
			enableReinitialize={true}
			initialValues={organizationData}
			validationSchema={validationSchema}
			onSubmit={(values): void => submitClickHandler(values)}
		>
			{({ errors, touched, setFieldValue, setFieldTouched, dirty }) => (
				<Form className={styles.form}>
					<Field name="organizationName">
						{({ field }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="organizationName">
									Organization name
								</label>
								<InputText
									id="organizationName"
									field={field}
									placeholder="fill in..."
									fieldValue={field.value}
									setFieldValue={setFieldValue}
								/>
								{errors.organizationName && touched.organizationName && (
									<InputErrorHint errorText={errors.organizationName} />
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="organisationGeneralType">
						{({ field }: FieldProps) => (
							<InputContainer parentClassnames={styles.typeInputContainer}>
								<label className={styles.label} htmlFor="organisationGeneralType">
									Type
								</label>
								<OrganizationTypeSelect
									id="organisationGeneralType"
									fieldName={field.name}
									fieldValue={field.value}
									setFieldValue={setFieldValue}
									setFieldTouched={setFieldTouched}
								/>
								{errors.organisationGeneralType && touched.organisationGeneralType && (
									<InputErrorHint errorText={errors.organisationGeneralType as string} />
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="organizationLogoPath">
						{({ field }: FieldProps) => (
							<InputContainer uploadLogoStyle={true}>
								<label className={styles.label} htmlFor="organizationLogoPath">
									Add your organization logo
								</label>
								<InputLogo
									id="organizationLogoPath"
									field={field}
									setFieldValue={setFieldValue}
									setFieldTouched={setFieldTouched}
									fieldTouched={touched.organizationLogoPath}
									fieldError={errors.organizationLogoPath}
								/>
							</InputContainer>
						)}
					</Field>
					<Field name="organizationDescription">
						{({ field }: FieldProps) => (
							<InputContainer textAreaStyle={true}>
								<label className={styles.label} htmlFor="organizationDescription">
									About the Organization
								</label>
								<InputTextArea
									id="organizationDescription"
									field={field}
									placeholder="fill in..."
									setFieldValue={setFieldValue}
								/>
								{errors.organizationDescription && touched.organizationDescription && (
									<InputErrorHint errorText={errors.organizationDescription} />
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="organizationPhoneNumber">
						{({ field }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="organizationPhoneNumber">
									Phone number
								</label>
								<InputPhone
									fieldName={field.name}
									fieldValue={field.value}
									setFieldValue={setFieldValue}
									setFieldTouched={setFieldTouched}
								/>
								{errors.organizationPhoneNumber && touched.organizationPhoneNumber && (
									<InputErrorHint errorText={errors.organizationPhoneNumber} />
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="organizationEmail">
						{({ field }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="organizationEmail">
									Email address
								</label>
								<InputText
									id="organizationEmail"
									field={field}
									placeholder="fill in..."
									fieldValue={field.value}
									setFieldValue={setFieldValue}
								/>
								{errors.organizationEmail && touched.organizationEmail && (
									<InputErrorHint errorText={errors.organizationEmail} />
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="organizationWebSite">
						{({ field }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="organizationWebSite">
									Website Url
								</label>
								<InputText
									id="organizationWebSite"
									field={field}
									placeholder="fill in..."
									fieldValue={field.value}
									setFieldValue={setFieldValue}
								/>
								{errors.organizationWebSite && touched.organizationWebSite && (
									<InputErrorHint errorText={errors.organizationWebSite} />
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="address">
						{({ field }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="organizationWebSite">
									Add the organization location
								</label>
								<GoogleApiAutoCompleat
									fieldName={field.name}
									selectHandler={address => setFieldValue(field.name, address)}
									fieldValue={field.value}
								/>
								{errors.address && touched.address && (
									<InputErrorHint addressErrorText={errors.address} />
								)}
							</InputContainer>
						)}
					</Field>
					{isPending ? (
						<Loader loadProps={{ disableCenterStyle: true }} />
					) : (
						<ButtonDefault
							submitType={true}
							classList={['primary', 'lg']}
							title="Confirm"
							disabled={!dirty}
						/>
					)}
				</Form>
			)}
		</Formik>
	);
};

export default OrgInfoForm;
